import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FiArrowLeft, FiArrowRight } from 'react-icons/fi';
import NotFound from './NotFound';
import AdSlot from '../components/AdSlot';
import usePageMeta from '../utils/usePageMeta';
import JsonLd from '../components/JsonLd';
import { articleSchema, breadcrumbSchema } from '../utils/schema';
import { getPostBySlug, formatPostDate } from '../data/blog';
import { services } from '../data/site';

const renderBlock = (block, index) => {
  if (block.h2) return <h2 key={index}>{block.h2}</h2>;
  if (block.list) {
    return (
      <ul key={index}>
        {block.list.map((item) => (
          <li key={item}>{item}</li>
        ))}
      </ul>
    );
  }
  return <p key={index}>{block.p}</p>;
};

const BlogDetail = () => {
  const { slug } = useParams();
  const post = getPostBySlug(slug);

  usePageMeta(post?.title, post?.excerpt, { type: 'article' });

  if (!post) {
    return <NotFound />;
  }

  const relatedService = services.find((service) => service.id === post.relatedService);
  // Mid-article ad goes just before a section heading roughly a third of the way in,
  // so it never separates a heading from its content.
  const midAdIndex = post.body.findIndex((block, index) => block.h2 && index >= Math.floor(post.body.length / 3));

  return (
    <article className="blog-detail">
      <JsonLd
        data={[
          articleSchema(post),
          breadcrumbSchema([
            { name: 'Home', path: '/' },
            { name: 'Blog', path: '/blog' },
            { name: post.title, path: `/blog/${post.slug}` }
          ])
        ]}
      />
      <div className="container">
        <motion.div
          initial={{ y: 16 }}
          animate={{ y: 0 }}
          transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
        >
          <Link to="/blog" className="back-link"><FiArrowLeft /> All articles</Link>

          <header className="blog-header">
            <span className="blog-category">{post.category}</span>
            <h1>{post.title}</h1>
            <div className="blog-meta">
              <span>{post.author}</span>
              <span>·</span>
              <time dateTime={post.date}>{formatPostDate(post.date)}</time>
              <span>·</span>
              <span>{post.readTime}</span>
            </div>
          </header>

          <div className="blog-body">
            {post.body.map((block, index) => (
              <React.Fragment key={index}>
                {index === midAdIndex && <AdSlot key={`${post.slug}-mid`} type="article" />}
                {renderBlock(block, index)}
              </React.Fragment>
            ))}
          </div>

          <AdSlot key={`${post.slug}-end`} type="display" />

          {relatedService && (
            <aside className="blog-cta">
              <div>
                <span className="eyebrow">Need help with this?</span>
                <h3>{relatedService.title} by Fedigma</h3>
                <p>{relatedService.description}</p>
              </div>
              <Link to={relatedService.link} className="btn">
                Explore the service <FiArrowRight />
              </Link>
            </aside>
          )}
        </motion.div>
      </div>
    </article>
  );
};

export default BlogDetail;
